import { useTranslation } from "react-i18next";
import Select from "react-select";
import { FilterPopover } from "../../../shared";
import type { ThemeClasses } from "../../../shared";
import type { StylesConfig } from "react-select";
import { useDisableCRUD } from "../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../components/base/CRUDTooltip";

interface NotificationsHeaderProps {
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  customSelectStyles: StylesConfig;
  selectedType: string;
  setSelectedType: (value: string) => void;
  selectedStatus: string;
  setSelectedStatus: (value: string) => void;
  search: string;
  setSearch: (value: string) => void;
  onAddClick: () => void;
  onSendToAllClick?: () => void;
  isReseller?: boolean;
  selectedView: "grid" | "list";
  setSelectedView: (view: "grid" | "list") => void;
}

export default function NotificationsHeader({
  isDarkMode,
  themeClasses,
  customSelectStyles,
  selectedType,
  setSelectedType,
  selectedStatus,
  setSelectedStatus,
  search,
  setSearch,
  onAddClick,
  onSendToAllClick,
  isReseller,
  selectedView,
  setSelectedView,
}: NotificationsHeaderProps) {
  const { t } = useTranslation("adminSections");
  const disableCRUD = useDisableCRUD();
  const notificationTypes = [
    { value: "all", label: t("notifications.fields.all") },
    { value: "specific", label: t("notifications.fields.specificUsers") },
  ];

  const statusOptions = [
    { value: "all", label: t("notifications.status.all") },
    { value: "scheduled", label: t("notifications.status.scheduled") },
    { value: "sent", label: t("notifications.status.sent") },
  ];

  return (
    <div className={`${themeClasses.bg} px-4 py-3 sm:px-6 sm:py-4`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-3">
        <div className="min-w-0">
          <h1 className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}>
            {t("notifications.title")}
          </h1>
          <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
            {t("notifications.subtitle")}
          </p>
        </div>
        <div className="flex gap-2 self-start sm:self-auto flex-shrink-0">
          {isReseller && onSendToAllClick && disableCRUD && (
            <button
              onClick={onSendToAllClick}
              className={`${
                isDarkMode 
                  ? "bg-blue-600 hover:bg-blue-700" 
                  : "bg-blue-500 hover:bg-blue-600"
              } text-white px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors whitespace-nowrap text-sm sm:text-base`}
            >
              <i className="ri-global-line"></i>
              <span className="hidden sm:inline">{t("notifications.sendToAll")}</span>
              <span className="sm:hidden">Todos</span>
            </button>
          )}
          <CRUDTooltip>
            <button
              onClick={onAddClick}
              disabled={disableCRUD}
              className="bg-primary-500 hover:bg-primary-400 text-gray-900 px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors whitespace-nowrap text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-add-line"></i>
              <span className="hidden sm:inline">{t("notifications.addNotification")}</span>
              <span className="sm:hidden">Nueva</span>
            </button>
          </CRUDTooltip>
        </div>
      </div>

      <div className="flex items-center gap-2 min-w-0">
        <div className="relative flex-1 min-w-0 sm:flex-none sm:w-72">
          <i
            className={`ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textSecondary} text-sm`}
          ></i>
          <input
            type="text"
            placeholder={t("notifications.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`pl-9 pr-3 py-1.5 h-9 ${
              isDarkMode ? "bg-dark-card" : "bg-gray-50"
            } border ${themeClasses.cardBorder} rounded-lg ${
              themeClasses.text
            } placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm w-full`}
          />
        </div>

        <FilterPopover
          isDarkMode={isDarkMode}
          activeCount={
            (selectedType !== "all" ? 1 : 0) + (selectedStatus !== "all" ? 1 : 0)
          }
          onClear={() => {
            setSelectedType("all");
            setSelectedStatus("all");
          }}
        >
          <Select
            options={notificationTypes}
            value={notificationTypes.find(
              (type) => type.value === selectedType
            )}
            onChange={(option: any) => setSelectedType(option.value)}
            styles={customSelectStyles}
            isSearchable={false}
            placeholder={t("notifications.filterByType")}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />

          <Select
            options={statusOptions}
            value={statusOptions.find(
              (status) => status.value === selectedStatus
            )}
            onChange={(option: any) => setSelectedStatus(option.value)}
            styles={customSelectStyles}
            isSearchable={false}
            placeholder={t("notifications.filterByStatus")}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </FilterPopover>

        {/* View Toggle */}
        <div className="ml-auto flex shrink-0">
      <div
        className={`flex ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg p-1`}
      >
        <button
          className={`px-3 py-1 rounded-md transition-colors ${
            selectedView === "grid"
              ? "bg-primary-500 text-gray-900"
              : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
          }`}
          onClick={() => setSelectedView("grid")}
        >
          <i className="ri-grid-fill"></i>
        </button>
        <button
          className={`px-3 py-1 rounded-md transition-colors ${
            selectedView === "list"
              ? "bg-primary-500 text-gray-900"
              : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
          }`}
          onClick={() => setSelectedView("list")}
        >
          <i className="ri-list-check"></i>
        </button>
      </div>
    </div>
      </div>
    </div>
  );
}
